import { useState } from "react";
import { apiRequest, isAbortError } from "../../lib/apiClient";
import { useCollection } from "../../lib/useCollection";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { AsyncSection } from "../../components/ui/AsyncSection";
import { TextField } from "../../components/ui/Field";

const EMPTY_FORM = { id: "", title: "" };

export function VideosPanel() {
  const { status, data: videos, error, reload, setData } = useCollection(
    "/api/videos",
    (payload) => (Array.isArray(payload) ? payload : [])
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const toast = useToast();
  const confirm = useConfirm();

  const updateField = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const handleAdd = async (event) => {
    event.preventDefault();

    if (!form.id.trim() || !form.title.trim()) {
      toast.error("Both a YouTube ID and a title are required.");
      return;
    }

    setSaving(true);
    try {
      const created = await apiRequest("/api/videos/add", {
        method: "POST",
        auth: true,
        body: { id: form.id.trim(), title: form.title.trim() },
      });

      setData((current) => [created, ...current]);
      setForm(EMPTY_FORM);
      toast.success("Video added.");
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (video) => {
    const confirmed = await confirm({
      title: "Delete this video?",
      message: `“${video.title}” will be removed from the homepage.`,
    });
    if (!confirmed) return;

    try {
      await apiRequest(`/api/videos/delete/${video._id}`, { method: "DELETE", auth: true });
      setData((current) => current.filter((item) => item._id !== video._id));
      toast.success("Video deleted.");
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    }
  };

  return (
    <section className="panel" aria-labelledby="videos-heading">
      <div className="upload-card">
        <h2 id="videos-heading">Homepage videos</h2>

        <form onSubmit={handleAdd}>
          <div className="input-row">
            <TextField
              label="YouTube ID"
              hint="The part after v= in the URL, e.g. N3QzaUwml5w"
              value={form.id}
              onChange={updateField("id")}
              disabled={saving}
            />

            <TextField
              label="Video title"
              value={form.title}
              onChange={updateField("title")}
              disabled={saving}
            />
          </div>

          <button type="submit" disabled={saving}>
            {saving ? "Adding…" : "Add video"}
          </button>
        </form>
      </div>

      <AsyncSection
        status={status}
        error={error}
        isEmpty={videos?.length === 0}
        emptyMessage="No videos yet."
        onRetry={reload}
      >
        <ul className="slides-grid">
          {videos?.map((video) => (
            <li key={video._id} className="slide-card">
              <iframe
                width="100%"
                height="170"
                src={`https://www.youtube.com/embed/${video.id}`}
                title={video.title}
                frameBorder="0"
                allowFullScreen
              />
              <h3 className="slide-card__title">{video.title}</h3>
              <button
                type="button"
                className="delete-btn"
                onClick={() => handleDelete(video)}
                aria-label={`Delete video: ${video.title}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </AsyncSection>
    </section>
  );
}
